import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface VersionInfo {
  version: string;
  commit_hash: string;
  branch: string;
  build_time: string;
  environment: string;
}

interface LayoutProps {
  children: React.ReactNode;
  isChatOpen?: boolean;
  isChatCollapsed?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, isChatOpen = false, isChatCollapsed = false }) => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  
  // Calculate chat width for margin adjustment
  const getChatWidth = () => {
    if (!isChatOpen) return 0;
    return isChatCollapsed ? 48 : 480; // 12 for collapsed (w-12), 480px for expanded (w-[480px])
  };

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await api.get('/api/version');
        setVersionInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch version info:', error);
        // Fallback to direct fetch if API call fails
        try {
          // Use the same API URL logic as the main API service
          const getApiBaseUrl = () => {
            if (process.env.NODE_ENV === 'production') {
              return '/api';
            }
            return process.env.REACT_APP_API_URL || 'http://localhost:8001';
          };
          
          const API_BASE_URL = getApiBaseUrl();
          const endpoint = process.env.NODE_ENV === 'production' ? '/version' : '/api/version';
          
          const fallbackResponse = await fetch(`${API_BASE_URL}${endpoint}`);
          const fallbackData = await fallbackResponse.json();
          setVersionInfo(fallbackData);
        } catch (fallbackError) {
          console.error('Fallback fetch also failed:', fallbackError);
        }
      }
    };

    fetchVersion();
  }, []);

  return (
    <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-900 font-inter flex flex-col">
      {/* Main content */}
      <main 
        className="overflow-y-auto flex flex-col flex-1 transition-all duration-300"
        style={{ 
          scrollbarGutter: 'stable'
        }}
      >
        <div className="flex-1 p-6 pb-2">
          <div className={`mx-auto ${
            window.location.pathname === '/trends' || window.location.pathname === '/insights' 
              ? 'max-w-none' 
              : 'max-w-7xl'
          }`}>
            {children}
          </div>
        </div>
        
        {/* Version Info - always at bottom with reduced spacing */}
        {versionInfo && (
          <div className="text-center py-1">
            <span className="text-xs text-gray-400 dark:text-gray-600 font-inter">
              v{versionInfo.version}-{versionInfo.commit_hash}
              {versionInfo.environment === 'development' && ' • dev'}
            </span>
          </div>
        )}
      </main>
    </div>
  );
};

export default Layout; 